import { useTranslation } from 'react-i18next';
import { ServicoMaisVendido, formatarValorEUR } from '@/hooks/useDashboardFinanceiro';

interface GraficoServicosPizzaProps {
  dados: ServicoMaisVendido[];
}

/**
 * Mix de serviços renderizado como lista de barras monocromáticas (mesma
 * família visual dos gráficos de status). Top 5 serviços + "Outros" agregando
 * o resto, para evitar uma lista interminável quando o catálogo é grande.
 */
export function GraficoServicosPizza({ dados }: GraficoServicosPizzaProps) {
  const { t } = useTranslation();

  if (!dados || dados.length === 0) {
    return (
      <div className="h-[200px] w-full flex items-center justify-center text-muted-foreground text-sm">
        {t('common.noData')}
      </div>
    );
  }

  // Top 5 + "Outros" como agregado.
  const top = dados.slice(0, 5);
  const outrosValor = dados.slice(5).reduce((acc, s) => acc + s.valorTotal, 0);

  const linhas = [
    ...top.map((s) => ({ nome: s.servicoNome, valor: s.valorTotal })),
    ...(outrosValor > 0
      ? [{ nome: t('dashboard.charts.others'), valor: outrosValor }]
      : []),
  ];

  const total = linhas.reduce((acc, l) => acc + l.valor, 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{t('dashboard.charts.servicesMix')}</span>
        <span className="tabular-nums font-mono">{formatarValorEUR(total)}</span>
      </div>

      <ul className="flex flex-col gap-2.5">
        {linhas.map((linha) => {
          const pct = total > 0 ? (linha.valor / total) * 100 : 0;
          return (
            <li key={linha.nome} className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-foreground font-medium truncate" title={linha.nome}>
                  {linha.nome}
                </span>
                <span className="text-muted-foreground tabular-nums font-mono text-xs">
                  {formatarValorEUR(linha.valor)} · {pct.toFixed(0)}%
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-foreground rounded-full transition-[width] duration-300"
                  style={{ width: `${pct}%` }}
                  aria-label={`${pct.toFixed(0)}%`}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
